import React from 'react';
import { ExternalLink, Download, ArrowRight } from 'lucide-react';

const RecursosPage = () => {
  const articulos = [
    {
      title: "Estrategia de Datos: Construyendo Puentes",
      excerpt: "Cómo conectar efectivamente los datos con los objetivos estratégicos de tu empresa.",
      url: "https://www.linkedin.com/pulse/estrategia-de-datos-construyendo-puentes-entre-y-campos-navarro-ik0me/",
      date: "15 Mar 2025"
    },
    {
      title: "La Crisis Silenciosa del Data Management",
      excerpt: "Por qué muchas empresas fallan en sus iniciativas de datos y cómo evitarlo.",
      url: "https://www.linkedin.com/pulse/la-crisis-silenciosa-del-data-management-por-qu%C3%A9-tu-campos-navarro-gzoue/",
      date: "28 Feb 2025"
    },
    {
      title: "Inteligencia Artificial para PyMEs",
      excerpt: "Guía práctica para implementar IA en pequeñas y medianas empresas con presupuestos limitados.",
      url: "#",
      date: "10 Feb 2025"
    },
    {
      title: "El ROI de los Datos: Más Allá de las Métricas Obvias",
      excerpt: "Cómo medir el verdadero impacto de tus inversiones en datos y analítica.",
      url: "#",
      date: "25 Ene 2025"
    }
  ];

  const podcasts = [
    {
      title: "Impacto de los Datos y la Tecnología en los Negocios",
      excerpt: "Conversación sobre cómo los datos están transformando modelos de negocio tradicionales.",
      url: "https://creators.spotify.com/pod/show/creativos/episodes/Impacto-d-elos-datos-y-la-tecnologa-en-los-negocios-e2v2l25",
      platform: "Spotify"
    },
    {
      title: "La Evolución de los Datos en la Era Digital",
      excerpt: "Análisis de tendencias y futuro de la analítica de datos en empresas latinoamericanas.",
      url: "https://creators.spotify.com/pod/show/creativos/episodes/Impacto-de-los-datos-y-la-tecnologa-en-los-negocios-e2v2l25/a-abpoual",
      platform: "Spotify"
    }
  ];

  const recursos = [
    {
      title: "Guía: Primeros Pasos en Data Strategy",
      description: "PDF con framework práctico para desarrollar tu estrategia de datos desde cero.",
      type: "PDF"
    },
    {
      title: "Template: Evaluación de Madurez Analítica",
      description: "Herramienta para diagnosticar el nivel de madurez analítica de tu organización.",
      type: "Excel"
    },
    {
      title: "Checklist: Implementación de Proyectos de IA",
      description: "Lista de verificación para asegurar el éxito en proyectos de inteligencia artificial.",
      type: "PDF"
    }
  ];

  return (
    <>
      {/* Header */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#1A365D] to-[#2A4A7F] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Recursos y Conocimiento</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Artículos, podcasts y herramientas para potenciar tu estrategia de datos
          </p>
        </div>
      </section>

      {/* Artículos */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#1A365D] mb-12">Artículos Destacados</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {articulos.map((articulo, index) => (
              <a 
                key={index}
                href={articulo.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all border border-gray-100"
              >
                <div className="text-sm text-gray-500 mb-2">{articulo.date}</div>
                <h3 className="text-xl font-bold text-[#1A365D] mb-3">{articulo.title}</h3>
                <p className="text-gray-600 mb-4">{articulo.excerpt}</p>
                <div className="flex items-center text-[#4A90E2]">
                  <span>Leer más</span>
                  <ExternalLink className="ml-2 h-4 w-4" />
                </div>
              </a>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="#" className="text-[#4A90E2] font-semibold inline-flex items-center">
              Ver todos los artículos <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Podcasts */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#1A365D] mb-12">Episodios del Podcast</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {podcasts.map((podcast, index) => (
              <a 
                key={index}
                href={podcast.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                <div className="flex items-center mb-4">
                  <div className="bg-[#1A365D] text-white px-3 py-1 rounded text-sm">{podcast.platform}</div>
                </div>
                <h3 className="text-xl font-bold text-[#1A365D] mb-3">{podcast.title}</h3>
                <p className="text-gray-600 mb-4">{podcast.excerpt}</p>
                <div className="flex items-center text-[#4A90E2]">
                  <span>Escuchar episodio</span>
                  <ExternalLink className="ml-2 h-4 w-4" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Recursos Descargables */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#1A365D] mb-12">Recursos Gratuitos</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {recursos.map((recurso, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all border border-gray-100">
                <div className="bg-gray-100 text-[#1A365D] px-3 py-1 rounded text-sm inline-block mb-4">{recurso.type}</div>
                <h3 className="text-xl font-bold text-[#1A365D] mb-3">{recurso.title}</h3>
                <p className="text-gray-600 mb-6">{recurso.description}</p>
                <button className="bg-[#4A90E2] text-white px-4 py-2 rounded-md hover:bg-[#3A80D2] transition-colors flex items-center">
                  <Download className="mr-2 h-4 w-4" />
                  Descargar gratis
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-[#1A365D] text-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-[#2A4A7F] p-8 md:p-12 rounded-lg">
            <h2 className="text-3xl font-bold mb-6 text-center">Suscríbete a Nuestro Newsletter</h2>
            <p className="text-center text-gray-300 mb-8">
              Recibe mensualmente artículos, casos de estudio y recursos exclusivos sobre estrategia de datos.
            </p>
            <form className="flex flex-col md:flex-row gap-4">
              <input 
                type="email" 
                placeholder="Tu correo electrónico" 
                className="flex-grow px-4 py-3 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4A90E2]"
              />
              <button 
                type="submit"
                className="bg-[#27AE60] px-6 py-3 rounded-md hover:bg-[#219653] transition-colors"
              >
                Suscribirme
              </button>
            </form>
            <p className="text-sm text-gray-400 mt-4 text-center">
              Respetamos tu privacidad. Puedes darte de baja en cualquier momento.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default RecursosPage;